import React, { useCallback, useEffect, useState } from "react";
import { Ban, BadgeCheck, CheckCircle2, ChevronDown, Copy, Download, Search, SlidersHorizontal, Undo2, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { HEAD, Pill, PdLoading, cdFilename, copyText, mask } from "@/components/PdPill";

const POLL_MS = 15000;
const DATES = [["today", "Today"], ["yesterday", "Yesterday"], ["last7", "Last 7d"], ["all", "All"], ["custom", "Custom"]];
const STATUSES = [["correct", "Done"], ["wrong", "DAC ✗"], ["cancelled", "Cancelled"], ["resolved", "Resolved"], ["all", "All"]];
const ST = {
  correct: ["DONE", "text-[#385C4B]", CheckCircle2, "bg-[#385C4B]/10"],
  wrong: ["DAC ✗", "text-[#A8620F]", XCircle, "bg-[#E89B35]/15"],
  cancelled: ["CANCELLED", "text-[#8A6046]", Ban, "bg-[#8A6046]/10"],
  resolved: ["RESOLVED", "text-[#2E6B8A]", BadgeCheck, "bg-[#2E6B8A]/10"],
};
const fmtFull = (iso) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
const todayIst = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const initF = () => ({ date: "today", from_date: todayIst(), to_date: todayIst(), time_from: "", time_to: "", status: "correct", agency: "all", q: "" });
const inputCls = "h-10 px-3 text-sm border border-[#E0DFD6] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30";
const lbl = "text-[10px] font-semibold uppercase tracking-[0.18em] text-[#6B6B61]";
const card = "bg-white border border-[#E5E5DF] rounded-xl shadow-sm";

export function DoneTab({ hide, isAdmin, company, onChanged, compact = false }) {
  const [f, setF] = useState(initF);
  const [qDeb, setQDeb] = useState("");
  const [data, setData] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { const t = setTimeout(() => setQDeb(f.q), 400); return () => clearTimeout(t); }, [f.q]);
  const pk = JSON.stringify({ ...f, q: qDeb, company });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pending-deliveries/done", { params: JSON.parse(pk) });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [pk]);
  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const undo = async (r) => {
    try {
      await api.post("/pending-deliveries/undo", { group_id: r.group_id, message_id: r.message_id, entry_index: r.entry_index });
      toast.success("Undo ho gaya — wapas Pending");
      await Promise.all([load(), onChanged()]);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const exportXlsx = async () => {
    try {
      const res = await api.get("/pending-deliveries/done/export.xlsx", { params: JSON.parse(pk), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = cdFilename(res, "DONE.xlsx");
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const agencies = data?.agencies || [];

  return (
    <div className="space-y-4 animate-in fade-in duration-300" data-testid="pd-done-tab">
      {compact ? (
        <CompactBar f={f} set={set} agencies={agencies} reset={() => setF(initF())} />
      ) : (
        <div className={`${card} p-4 space-y-4`} data-testid="pd-done-filters">
          <FilterFields f={f} set={set} agencies={agencies} wide />
          <div className="flex gap-2">
            <SearchBox value={f.q} onChange={(v) => set("q", v)} />
            {isAdmin && (
              <button onClick={exportXlsx} data-testid="pd-done-export" className="shrink-0 inline-flex items-center gap-1.5 h-10 px-4 text-sm font-semibold rounded-lg bg-[#385C4B] text-white hover:bg-[#2D4A3C] transition-colors">
                <Download className="w-4 h-4" />Excel
              </button>
            )}
          </div>
        </div>
      )}
      {!data ? <PdLoading /> : (
        <>
          {compact ? <CompactSummary data={data} agency={f.agency} pick={(id) => set("agency", f.agency === id ? "all" : id)} />
            : <AdminSummary data={data} isAdmin={isAdmin} pick={(id) => set("agency", id)} />}
          <DoneList data={data} hide={hide} undo={undo} />
        </>
      )}
    </div>
  );
}

function SearchBox({ value, onChange }) {
  return (
    <div className="relative flex-1">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A80]" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Search agency / LPG ID / mobile / DAC…" data-testid="pd-done-search"
        className="w-full h-10 pl-9 pr-3 text-sm bg-white border border-[#E0DFD6] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#385C4B]/30" />
    </div>
  );
}

const Field = ({ label, children }) => <div><div className={`${lbl} mb-1.5`}>{label}</div>{children}</div>;

function FilterFields({ f, set, agencies, wide }) {
  return (
    <div className={wide ? "grid gap-4 lg:grid-cols-2" : "space-y-5"}>
      <Field label="Date">
        <div className="flex flex-wrap gap-2">
          {DATES.map(([k, l]) => <Pill key={k} tone="brown" on={f.date === k} onClick={() => set("date", k)} testid={`pd-done-date-${k}`}>{l}</Pill>)}
        </div>
        {f.date === "custom" && (
          <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="pd-done-custom">
            <input type="date" value={f.from_date} onChange={(e) => set("from_date", e.target.value)} className={inputCls} data-testid="pd-done-from-date" />
            <span className="text-[#6B6B61]">→</span>
            <input type="date" value={f.to_date} min={f.from_date} onChange={(e) => set("to_date", e.target.value)} className={inputCls} data-testid="pd-done-to-date" />
          </div>
        )}
      </Field>
      <Field label="Time (delivered)">
        <div className="flex items-center gap-2">
          <input type="time" value={f.time_from} onChange={(e) => set("time_from", e.target.value)} className={inputCls} data-testid="pd-done-time-from" />
          <span className="text-[#6B6B61]">–</span>
          <input type="time" value={f.time_to} onChange={(e) => set("time_to", e.target.value)} className={inputCls} data-testid="pd-done-time-to" />
          {(f.time_from || f.time_to) && (
            <button onClick={() => { set("time_from", ""); set("time_to", ""); }} className="grid place-items-center w-10 h-10 rounded-lg border border-[#E0DFD6] bg-white" data-testid="pd-done-time-clear" aria-label="Clear time">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </Field>
      <Field label="Status">
        <div className="flex flex-wrap gap-2">
          {STATUSES.map(([k, l]) => <Pill key={k} on={f.status === k} onClick={() => set("status", k)} testid={`pd-done-status-${k}`}>{l}</Pill>)}
        </div>
      </Field>
      <Field label="Agency">
        <select value={f.agency} onChange={(e) => set("agency", e.target.value)} className={`${inputCls} w-full max-w-sm`} data-testid="pd-done-agency">
          <option value="all">All agencies</option>
          {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </Field>
    </div>
  );
}

function CompactBar({ f, set, agencies, reset }) {
  const [open, setOpen] = useState(false);
  const active = [f.date !== "today", f.time_from || f.time_to, f.status !== "correct", f.agency !== "all"].filter(Boolean).length;
  const agencyName = agencies.find((a) => a.id === f.agency)?.name;
  const parts = [
    f.date === "custom" ? `${f.from_date} → ${f.to_date}` : DATES.find(([k]) => k === f.date)?.[1],
    STATUSES.find(([k]) => k === f.status)?.[1],
    f.time_from || f.time_to ? `${f.time_from || "00:00"}–${f.time_to || "23:59"}` : "",
    agencyName || "",
  ].filter(Boolean);
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <SearchBox value={f.q} onChange={(v) => set("q", v)} />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="shrink-0 inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg bg-[#385C4B] text-white text-sm font-semibold active:scale-95 transition-transform" data-testid="pd-done-filters-btn">
              <SlidersHorizontal className="w-4 h-4" />Filters
              {active > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-white text-[#385C4B] text-[10px] font-mono leading-[18px] text-center" data-testid="pd-done-filters-count">{active}</span>}
              <ChevronDown className="w-4 h-4" />
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-3xl max-h-[88vh] overflow-y-auto bg-[#FBFBF7] pb-[calc(1.5rem+env(safe-area-inset-bottom))]" data-testid="pd-done-filters">
            <SheetHeader className="text-left">
              <SheetTitle className={`${HEAD} text-xl`}>Filters</SheetTitle>
              <SheetDescription className="text-xs">Delivered date / time, status aur agency choose karo.</SheetDescription>
            </SheetHeader>
            <div className="mt-5"><FilterFields f={f} set={set} agencies={agencies} /></div>
            <div className="grid grid-cols-2 gap-2 mt-7">
              <button onClick={reset} className="h-12 rounded-xl border border-[#E0DFD6] bg-white text-sm font-semibold" data-testid="pd-done-reset">Reset</button>
              <button onClick={() => setOpen(false)} className="h-12 rounded-xl bg-[#385C4B] text-white text-sm font-semibold" data-testid="pd-done-apply">Dekho</button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      <div className="text-xs text-[#6B6B61] truncate" data-testid="pd-done-active">{parts.join(" · ")}</div>
    </div>
  );
}

function ChipRow({ title, items, active, onPick, testid }) {
  if (!items.length) return null;
  return (
    <div data-testid={testid}>
      <div className={`${lbl} mb-1.5`}>{title}</div>
      <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-1">
        {items.map((i) => (
          <button key={i.key} onClick={onPick ? () => onPick(i.key) : undefined} data-testid={`${testid}-${i.key}`}
            className={`shrink-0 inline-flex items-center gap-2 h-9 px-3 rounded-full border text-xs transition-colors ${active === i.key ? "bg-[#385C4B] text-white border-[#385C4B]" : "bg-white border-[#E0DFD6] text-[#3A3A35]"} ${onPick ? "" : "cursor-default"}`}>
            <span className="max-w-[160px] truncate">{i.label}</span><b className="font-mono">{i.count}</b>
          </button>
        ))}
      </div>
    </div>
  );
}

function CompactSummary({ data, agency, pick }) {
  return (
    <div className="space-y-3" data-testid="pd-done-summary">
      <div className="rounded-2xl bg-[#385C4B] text-white p-4 flex items-end justify-between shadow-sm">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-white/75 font-semibold">Total</div>
          <div className={`${HEAD} text-4xl font-semibold tabular-nums leading-none mt-1`} data-testid="pd-done-total">{data.total}</div>
        </div>
        <CheckCircle2 className="w-8 h-8 text-white/40" />
      </div>
      <ChipRow title="Agency-wise" items={data.by_agency} active={agency} onPick={pick} testid="pd-done-by-agency" />
      <ChipRow title="Date-wise" items={data.by_date} testid="pd-done-by-date" />
    </div>
  );
}

function SumCard({ title, items, onPick, testid }) {
  return (
    <div className={`${card} p-4`} data-testid={testid}>
      <div className={`${lbl} mb-2`}>{title}</div>
      {items.length === 0 ? <div className="text-xs text-[#6B6B61]">—</div> : (
        <div className="space-y-0.5 max-h-48 overflow-y-auto">
          {items.map((i) => (
            <button key={i.key} onClick={onPick ? () => onPick(i.key) : undefined} data-testid={`${testid}-${i.key}`}
              className={`w-full flex items-center gap-2 text-sm text-left px-1.5 py-1 rounded ${onPick ? "hover:bg-[#F4F3EC]" : "cursor-default"}`}>
              <span className="truncate flex-1">{i.label}</span><b className="font-mono">{i.count}</b>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminSummary({ data, isAdmin, pick }) {
  return (
    <div className={`grid gap-3 sm:grid-cols-2 ${isAdmin ? "lg:grid-cols-4" : "lg:grid-cols-3"}`} data-testid="pd-done-summary">
      <div className="rounded-xl bg-[#385C4B] text-white p-4 shadow-sm">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/75 font-semibold">Total</div>
        <div className={`${HEAD} text-4xl font-semibold tabular-nums mt-2`} data-testid="pd-done-total">{data.total}</div>
      </div>
      <SumCard title="Agency-wise" items={data.by_agency} onPick={pick} testid="pd-done-by-agency" />
      <SumCard title="Date-wise" items={data.by_date} testid="pd-done-by-date" />
      {isAdmin && <SumCard title="Kisne kiya" items={data.by_marker} testid="pd-done-by-marker" />}
    </div>
  );
}

function DoneList({ data, hide, undo }) {
  if (!data.rows.length) {
    return <div className="rounded-2xl border border-dashed border-[#DCDCD3] bg-white/60 p-8 text-center text-sm text-[#6B6B61]" data-testid="pd-done-empty">Is filter mein koi entry nahi mili.</div>;
  }
  return (
    <div className={`${card} divide-y divide-[#F0F0EA] overflow-hidden`} data-testid="pd-done-list">
      {data.rows.map((r) => <DoneRow key={`${r.message_id}:${r.entry_index}`} r={r} hide={hide} undo={undo} />)}
      {data.total > data.rows.length && <div className="px-4 py-2.5 text-[11px] text-[#6B6B61]">Latest {data.rows.length} of {data.total} dikha rahe hain — filter lagao.</div>}
    </div>
  );
}

function DoneRow({ r, hide, undo }) {
  const [label, cls, Icon, bg] = ST[r.status] || [r.status, "", CheckCircle2, ""];
  return (
    <div className="px-4 py-3 flex items-center gap-3 hover:bg-[#FAFAF6] transition-colors" data-testid={`pd-done-row-${r.lpg_id}`}>
      <span className={`grid place-items-center w-9 h-9 rounded-full shrink-0 ${bg}`}><Icon className={`w-4 h-4 ${cls}`} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <button onClick={() => copyText(r.lpg_id, "LPG ID")} className="font-mono font-bold text-sm flex items-center gap-1 min-w-0" data-testid={`pd-done-copy-${r.lpg_id}`}>
            <span className="truncate">{mask(r.lpg_id, hide)}</span><Copy className="w-3 h-3 text-[#8A8A80] shrink-0" />
          </button>
          <span className={`text-[10px] font-bold tracking-wider ${cls}`}>{label}</span>
        </div>
        <div className="text-[11px] text-[#6B6B61] break-words mt-0.5">{r.agency} · {fmtFull(r.marked_at)} · {r.status === "resolved" ? `DAC ✗ by ${r.marked_by}` : r.marked_by}</div>
        {r.resolution_note && (
          <div className="text-[11px] text-[#2E6B8A] break-words mt-0.5" data-testid={`pd-done-resolution-${r.lpg_id}`}>↳ {r.resolution_note} · {r.resolved_by}</div>
        )}
      </div>
      {r.can_undo && (
        <button onClick={() => undo(r)} data-testid={`pd-done-undo-${r.lpg_id}`}
          className="inline-flex items-center gap-1 h-9 px-3 text-xs font-semibold rounded-lg border border-[#8A6046]/50 text-[#8A6046] bg-white hover:bg-[#8A6046]/10 shrink-0 transition-colors">
          <Undo2 className="w-3.5 h-3.5" />Undo
        </button>
      )}
    </div>
  );
}
